import { useState, useEffect, useCallback, useMemo } from 'react';
import { getRoadblockDashboard, exportRoadblockDashboard } from '../roadblockDashboardService';
import { formatDateToYyyyMmDd } from '../../../utils/formatDate';
import { isICCSupport, isTeamManager, getAssociatedTeams } from '../../auth';

// ─── Pure aggregation ───────────────────────────────────────────────────────
// Derives a summary object from already-filtered breakdown rows.
// Used when the API summary block does not reflect the active state/drilldown
// filter (the backend computes summary globally; breakdown is scoped correctly).
// Exported for unit testing
export function aggregateBreakdownRows(breakdown, fallbackSummary) {
    let totalOpen = 0, totalClosed = 0, totalProdImpact = 0, totalProdHours = 0;
    let weightedResSum = 0, resolvedForAvg = 0;
    const typeMap = {};

    for (const row of breakdown) {
        totalOpen       += row.open                         ?? 0;
        totalClosed     += row.resolved                     ?? 0;
        totalProdImpact += row.productionImpacting          ?? 0;
        totalProdHours  += row.totalProductionHoursImpacted ?? 0;

        const rc = row.resolved ?? 0;
        if (rc > 0 && row.avgResolutionDays != null) {
            weightedResSum += row.avgResolutionDays * rc;
            resolvedForAvg += rc;
        }

        for (const t of (row.byType || [])) {
            if (!typeMap[t.typeId]) {
                typeMap[t.typeId] = { typeId: t.typeId, label: t.label, count: 0, total: 0 };
            }
            typeMap[t.typeId].count += t.count ?? 0;
            typeMap[t.typeId].total += t.total ?? t.count ?? 0;
        }
    }

    return {
        ...fallbackSummary,
        totalOpen,
        totalClosedInRange:           totalClosed,
        totalProductionImpacting:     totalProdImpact,
        // Sum from rows; fall back to global value if rows don't carry this field
        totalProductionHoursImpacted: totalProdHours || fallbackSummary.totalProductionHoursImpacted,
        avgResolutionDaysOverall:     resolvedForAvg > 0 ? weightedResSum / resolvedForAvg : null,
        byType:                       Object.values(typeMap),
    };
}

// Maps API response fields to the shape expected by the dashboard components.
function normalizeData(result) {
    const normByType = (list) => (list || []).map(t => ({ ...t, total: t.count }));
    return {
        ...result,
        summary: result.summary
            ? { ...result.summary, byType: normByType(result.summary.byType) }
            : result.summary,
        breakdown: (result.breakdown || []).map(item => ({
            ...item,
            stateCode:           item.state,
            open:                item.totalOpen,
            total:               (item.totalOpen ?? 0) + (item.totalClosedInRange ?? 0),
            resolved:            item.totalClosedInRange,
            productionImpacting: item.totalProductionImpacting,
            avgResolutionDays:   item.avgResolutionDaysOverall,
            displayName:         item.marketName ?? item.teamName,
            name:                item.market ?? item.team,
            byType:              normByType(item.byType),
        })),
    };
}

function defaultDate(offsetDays) {
    const d = new Date();
    d.setDate(d.getDate() + offsetDays);
    return d;
}

export function useRoadblockReport() {
    const [data, setData] = useState(null);
    const [loaded, setLoaded] = useState(false);
    const [error, setError] = useState('');
    const [exporting, setExporting] = useState(false);

    // Filters — default: all open roadblocks, no date range
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [selectedState, setSelectedState] = useState(''); // '' = All States

    // Status pill: 'all' | 'open' | 'closed'
    const [statusFilter, setStatusFilter] = useState('open');

    // Drilldown: null (market by state) | { type: 'team', state, stateCode, market, marketName }
    const [drilldown, setDrilldown] = useState(null);

    // Auth claims are stable for the component lifetime — no need to recompute per render
    const canSeeAllTeams = isICCSupport() || !isTeamManager();
    const assignedTeamIds = canSeeAllTeams ? [] : getAssociatedTeams().map(t => t.id);

    const buildParams = useCallback(() => {
        const params = { level: 'market' };
        if (startDate) params.start_date = formatDateToYyyyMmDd(startDate);
        if (endDate)   params.end_date   = formatDateToYyyyMmDd(endDate);
        if (!canSeeAllTeams && assignedTeamIds.length > 0) {
            params.team_ids = assignedTeamIds.join(',');
        }
        if (drilldown) {
            params.level  = 'team';
            params.state  = drilldown.stateCode;
            params.market = drilldown.market;
        } else if (selectedState) {
            params.state = selectedState;
        }
        return params;
    }, [startDate, endDate, selectedState, drilldown]);

    const fetchReport = useCallback(async () => {
        setLoaded(false);
        setError('');
        try {
            const result = await getRoadblockDashboard(buildParams());
            setData(normalizeData(result));
        } catch (err) {
            setError(err.message);
        } finally {
            setLoaded(true);
        }
    }, [buildParams]);

    useEffect(() => {
        fetchReport();
    }, [fetchReport]);

    const handleDrillToTeam = useCallback((row) => {
        setDrilldown({
            type: 'team',
            stateCode: row.stateCode,
            stateName: row.stateName,
            market: row.market,
            marketName: row.marketName,
        });
        setStatusFilter('all');
    }, []);

    const handleResetDrilldown = useCallback(() => {
        setDrilldown(null);
        setStatusFilter('all');
    }, []);

    const handleExport = useCallback(async () => {
        setExporting(true);
        try {
            await exportRoadblockDashboard(buildParams());
        } catch (err) {
            setError(err.message);
        } finally {
            setExporting(false);
        }
    }, [buildParams]);

    // Re-aggregate from filtered breakdown rows when a state/drilldown is active,
    // because the API's summary block is always global (see aggregateBreakdownRows).
    const activeSummary = useMemo(() => {
        const summary = data?.summary ?? {};
        const breakdown = data?.breakdown ?? [];
        if (!selectedState && !drilldown) return summary;
        return aggregateBreakdownRows(breakdown, summary);
    }, [data, selectedState, drilldown]);

    return {
        data,
        loaded,
        error,
        exporting,
        activeSummary,
        canSeeAllTeams,
        startDate,
        setStartDate,
        endDate,
        setEndDate,
        selectedState,
        setSelectedState,
        statusFilter,
        setStatusFilter,
        drilldown,
        handleDrillToTeam,
        handleResetDrilldown,
        handleExport,
    };
}
